/**
 * SCM PageDesigner PostMessage Communication Module
 * 
 * This module enables client sites to communicate with SiteBuilder Page Designer
 * through secure PostMessage API for widget interaction and configuration.
 * 
 * Refactored to use modular components for maintainability and testability.
 */

var SCM = (typeof global !== 'undefined' && global.SCM) || SCM || {};
SCM.PageDesigner = SCM.PageDesigner || {};

SCM.PageDesigner.PostMessage = (function ($) {
    let _logger, _messenger, _scanner, _feedback;
    let _config = { initialized: false };
    
    return {
        init: initialize,
        configure: configure,
        destroy: destroy,
        isInitialized: isInitialized,
        getConfig: getConfig,
        _internal: {
            handleWidgetClick: _handleWidgetClick,
            validateOrigin: _validateOrigin,
            detectWidgets: _detectWidgets
        }
    };
    
    function initialize(options) {
        if (_config.initialized) {
            _logger && _logger.info('Already initialized');
            return;
        }
        _initializeComponents(options);
        _wireComponentInteractions();
        _startWidgetDetectionAndInteraction();
        
        _config.initialized = true;
        _logger.info('PageDesigner PostMessage initialized successfully');
    }
    
    function _initializeComponents(options) {
        const opts = options || {};
        
        try {
            _logger = SCM.DebugLogger.create('PageDesigner.PostMessage', { debugMode: opts.debugMode || false });
        } catch(e) {
            _logger.error && _logger.error('Failed to initialize DebugLogger:', e.message);
            _logger = { debug: function(){}, info: function(){}, warn: function(){}, error: function(){} };
        }
        
        try {
            _messenger = SCM.CrossFrameMessenger.create({ allowedOrigins: _loadAllowedOrigins(), timeout: opts.communicationTimeout || 5000, debugMode: opts.debugMode });
        } catch(e) {
            _logger.error && _logger.error('Failed to initialize CrossFrameMessenger:', e.message);
            _messenger = { 
                connect: function(){ return Promise.resolve(); }, 
                disconnect: function(){}, 
                send: function(){ return Promise.resolve(); }, 
                on: function(){}, 
                validateOrigin: function(){ return false; } 
            };
        }
        
        try {
            _scanner = SCM.WidgetScanner.create({ selectors: opts.widgetSelectors || ['[data-widget-name][data-widget-instance]'], excludeTypes: ['DynamicWidgetLayout'], debugMode: opts.debugMode });
        } catch(e) {
            _logger.error && _logger.error('Failed to initialize WidgetScanner:', e.message);
            _scanner = { scan: function(){ return []; }, on: function(){} };
        }
        
        try {
            _feedback = SCM.UIFeedback.create({ cssClasses: { interactive: 'sb-widget-interactive', hover: 'sb-widget-hover', selected: 'sb-widget-selected', error: 'sb-widget-error' }, debugMode: opts.debugMode });
        } catch(e) {
            _logger.error && _logger.error('Failed to initialize UIFeedback:', e.message);
            _feedback = { makeInteractive: function(){}, setSelected: function(){}, cleanup: function(){}, on: function(){} };
        }
        
        // Preserve initialized state while merging options
        const wasInitialized = _config.initialized;
        _config = Object.assign(_config, opts);
        _config.initialized = wasInitialized;
    }
    
    function _wireComponentInteractions() {
        if (typeof _messenger.on === 'function') {
            _messenger.on('message', _handleParentMessage);
        }
        
        _messenger.connect().catch(function(error) {
            _logger.warn('Handshake failed, continuing without connection establishment:', error.message);
        });
    }
    
    function _startWidgetDetectionAndInteraction() {
        const widgets = _scanner.scan();
        _logger.debug('Detected ' + widgets.length + ' widgets for interaction');
        
        // Make widgets interactive using the UIFeedback component
        if (widgets.length > 0) {
            // Extract DOM elements from widget objects for UIFeedback
            const widgetElements = widgets.map(function(widget) {
                return widget.element;
            });
            
            // Use UIFeedback to make widgets interactive (handles both click and hover)
            widgetElements.forEach(function(element) {
                _feedback.makeInteractive(element);
            });
            
            // Set up custom click handlers for PostMessage communication
            widgets.forEach(function(widget) {
                $(widget.element).off('click.pagedesigner').on('click.pagedesigner', function(event) {
                    _handleWidgetClick(widget.element, event);
                });
            });
        }
    }
    
    function _handleWidgetClick(widget, event) {
        const widgetData = _extractWidgetData(widget);
        if (widgetData) {
            _messenger.send('pageDesignerWidgetClick', { widgetData: widgetData, timestamp: Date.now() })
                .then(function() {
                    _logger.debug && _logger.debug('Widget click message sent successfully');
                })
                .catch(function(error) {
                    _logger.error && _logger.error('Failed to send widget click message:', error);
                });
            _feedback.setSelected(widget);
        }
    }
    
    function _handleParentMessage(data) {
        if (data.type === 'pageDesignerClearSelection' || data.type === 'pageDesignerWidgetSaved') {
            _feedback.clearAll();
        } else if (data.type === 'pageDesignerConfig') {
            configure(data.config);
        }
    }
    
    function _extractWidgetData(widget) {
        const name = $(widget).attr('data-widget-name'), instance = $(widget).attr('data-widget-instance');
        return name && instance ? { widgetName: name, instanceName: instance } : null;
    }
    
    function _loadAllowedOrigins() {
        const meta = $('meta[name="sitebuilder-origin"]').attr('content');
        return meta ? meta.split(',').map(o => o.trim()).filter(Boolean) : [];
    }
    
    function _sendDirectPostMessage(messageType, payload) {
        if (window.parent && window.parent !== window) {
            // Use original flat message format that SiteBuilder expects
            const message = {
                type: messageType,
                timestamp: payload.timestamp || Date.now(),
                widgetData: payload.widgetData
            };
            
            // Get allowed origins for security
            const allowedOrigins = _loadAllowedOrigins();
            const targetOrigin = allowedOrigins.length > 0 ? allowedOrigins[0] : '*';
            
            try {
                window.parent.postMessage(message, targetOrigin);
                _logger.debug && _logger.debug('Direct postMessage sent:', messageType);
            } catch (error) {
                _logger.error && _logger.error('Failed to send direct postMessage:', error.message);
            }
        } else {
            _logger.warn && _logger.warn('No parent window available for postMessage');
        }
    }
    
    function configure(options) { 
        if (options) {
            Object.assign(_config, options);
        } 
    }
    function destroy() { _messenger && _messenger.disconnect(); _feedback && _feedback.cleanup(); _config.initialized = false; }
    function isInitialized() { return _config.initialized; }
    function getConfig() { return JSON.parse(JSON.stringify(_config)); }
    function _validateOrigin(origin) { return _messenger ? _messenger.validateOrigin(origin) : false; }
    function _detectWidgets() { return _scanner ? _scanner.scan() : []; }

})(typeof jQuery !== 'undefined' ? jQuery : (typeof global !== 'undefined' && global.jQuery) || function(){});

// If we are in a module context, export this module (required for unit test)
'object' === typeof module && module.exports && (module.exports = SCM.PageDesigner.PostMessage);